import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  PAYU_TABS,
  type PayuTab,
  resolveTabFromUrl,
  tabToSearchParams,
} from './payuTabs';
import { motion } from 'framer-motion';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { toast } from '@/hooks/use-toast';
import { useBusinessAccountContext } from '@/hooks/useBusinessAccountContext';
import { useWorkspaceScopeId } from '@/hooks/useWorkspaceScopeId';
import { payuApi } from '@/lib/api/services/payu';
import { showApiError } from '@/lib/errors';
import { validatePaymentAmount, MAX_PAYMENT_LINK_AMOUNT } from '@/lib/constants/paymentLimits';
import { ApiError } from '@/lib/api';
import { DisconnectConfirmDialog } from '@/components/integrations/DisconnectConfirmDialog';
import {
  Loader2,
  RefreshCw,
  PlugZap,
  Unplug,
  ArrowLeft,
  Key,
  Eye,
  EyeOff,
  Link as LinkIcon,
  CreditCard,
  ExternalLink,
  Copy,
  Plus,
  Info,
  Undo2,
} from 'lucide-react';

type PayUMode = 'test' | 'prod';

interface ConnectionInfo {
  connected: boolean;
  mode?: PayUMode;
  accountName?: string | null;
  connectedAt?: string | null;
}

const Payu: React.FC<{ embedded?: boolean }> = ({ embedded = false }) => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { isLoading: accountsLoading } = useBusinessAccountContext();
  const workspaceScopeId = useWorkspaceScopeId();

  const [activeTab, setActiveTabState] = useState<PayuTab>(() =>
    resolveTabFromUrl(searchParams.get('tab'))
  );

  const setActiveTab = useCallback(
    (next: PayuTab) => {
      setActiveTabState(next);
      setSearchParams((prev) => tabToSearchParams(prev, next), { replace: true });
    },
    [setSearchParams]
  );

  useEffect(() => {
    const urlTab = resolveTabFromUrl(searchParams.get('tab'));
    if (urlTab !== activeTab) {
      setActiveTabState(urlTab);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);
  const [loadingData, setLoadingData] = useState(true);
  const [connectionInfo, setConnectionInfo] = useState<ConnectionInfo>({ connected: false });

  const [form, setForm] = useState({
    merchantKey: '',
    salt: '',
    mode: 'test' as PayUMode,
    accountName: '',
  });
  const [showSalt, setShowSalt] = useState(false);

  const [connecting, setConnecting] = useState(false);
  const [testing, setTesting] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const [disconnectDialogOpen, setDisconnectDialogOpen] = useState(false);

  const [linkDialogOpen, setLinkDialogOpen] = useState(false);
  const [linkForm, setLinkForm] = useState({
    amount: '',
    productinfo: '',
    firstname: '',
    email: '',
    phone: '',
    description: '',
  });
  const [generatedLink, setGeneratedLink] = useState<string | null>(null);
  const [generatingLink, setGeneratingLink] = useState(false);

  const linkAmountCheck = validatePaymentAmount(linkForm.amount);
  const linkAmountError =
    linkForm.amount.trim() === '' || linkAmountCheck.ok ? null : linkAmountCheck.error;

  const [payments, setPayments] = useState<any[]>([]);
  const [paymentsLoading, setPaymentsLoading] = useState(false);
  const [paymentsTotal, setPaymentsTotal] = useState(0);

  const [paymentLinks, setPaymentLinks] = useState<any[]>([]);
  const [paymentLinksLoading, setPaymentLinksLoading] = useState(false);
  const [paymentLinksTotal, setPaymentLinksTotal] = useState(0);

  const [refunds, setRefunds] = useState<any[]>([]);
  const [refundsLoading, setRefundsLoading] = useState(false);
  const [refundsTotal, setRefundsTotal] = useState(0);

  const [refundDialogOpen, setRefundDialogOpen] = useState(false);
  const [refundForm, setRefundForm] = useState({ paymentId: '', amount: '', reason: '' });
  const [refundResult, setRefundResult] = useState<
    | { paymentId: string; refundRefId: string; status: string; message?: string | null }
    | null
  >(null);
  const [refunding, setRefunding] = useState(false);

  const isConnected = connectionInfo.connected;

  const checkConnection = useCallback(async () => {
    if (!workspaceScopeId) {
      setConnectionInfo({ connected: false });
      setLoadingData(false);
      return;
    }
    setLoadingData(true);
    try {
      const res = await payuApi.checkConnection(workspaceScopeId);
      setConnectionInfo(res || { connected: false });
    } catch {
      setConnectionInfo({ connected: false });
    } finally {
      setLoadingData(false);
    }
  }, [workspaceScopeId]);

  useEffect(() => {
    if (!accountsLoading) checkConnection();
  }, [accountsLoading, checkConnection]);

  const loadPayments = useCallback(async () => {
    if (!workspaceScopeId) return;
    setPaymentsLoading(true);
    try {
      const res = await payuApi.listPayments(workspaceScopeId, { page: 1, limit: 50 });
      setPayments(Array.isArray(res?.data) ? res.data : []);
      setPaymentsTotal(res?.pagination?.total || 0);
    } catch {
      setPayments([]);
      setPaymentsTotal(0);
    } finally {
      setPaymentsLoading(false);
    }
  }, [workspaceScopeId]);

  useEffect(() => {
    if (activeTab === 'payments' && isConnected) loadPayments();
  }, [activeTab, isConnected, loadPayments]);

  const loadPaymentLinks = useCallback(async () => {
    if (!workspaceScopeId) return;
    setPaymentLinksLoading(true);
    try {
      const res = await payuApi.listPaymentLinks(workspaceScopeId, { page: 1, limit: 50 });
      setPaymentLinks(Array.isArray(res?.data) ? res.data : []);
      setPaymentLinksTotal(res?.pagination?.total || 0);
    } catch {
      setPaymentLinks([]);
      setPaymentLinksTotal(0);
    } finally {
      setPaymentLinksLoading(false);
    }
  }, [workspaceScopeId]);

  useEffect(() => {
    if (activeTab === 'links' && isConnected) loadPaymentLinks();
  }, [activeTab, isConnected, loadPaymentLinks]);

  const loadRefunds = useCallback(async () => {
    if (!workspaceScopeId) return;
    setRefundsLoading(true);
    try {
      const res = await payuApi.listRefunds(workspaceScopeId, { page: 1, limit: 50 });
      setRefunds(Array.isArray(res?.data) ? res.data : []);
      setRefundsTotal(res?.pagination?.total || 0);
    } catch {
      setRefunds([]);
      setRefundsTotal(0);
    } finally {
      setRefundsLoading(false);
    }
  }, [workspaceScopeId]);

  useEffect(() => {
    if (activeTab === 'refunds' && isConnected) loadRefunds();
  }, [activeTab, isConnected, loadRefunds]);

  const handleManualConnect = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!workspaceScopeId) {
      toast({ title: 'Select a business account first', variant: 'warning' });
      return;
    }
    if (!form.merchantKey.trim() || !form.salt.trim()) {
      toast({ title: 'Missing credentials', description: 'Enter both merchant key and salt.', variant: 'warning' });
      return;
    }
    setConnecting(true);
    try {
      await payuApi.connect({
        businessId: workspaceScopeId,
        merchantKey: form.merchantKey.trim(),
        salt: form.salt.trim(),
        mode: form.mode,
        accountName: form.accountName.trim() || undefined,
      });
      toast({ variant: "success", title: 'PayU connected', description: `Mode: ${form.mode}` });
      setForm({ merchantKey: '', salt: '', mode: 'test', accountName: '' });
      setShowSalt(false);
      await checkConnection();
    } catch (err: any) {
      showApiError(err, { fallback: { title: 'Connect failed' } });
    } finally {
      setConnecting(false);
    }
  };

  const handleTestConnection = async () => {
    if (!workspaceScopeId) return;
    setTesting(true);
    try {
      const res = await payuApi.checkConnection(workspaceScopeId);
      if (res?.connected) {
        toast({ title: 'Connection active', description: `Mode: ${res.mode || 'test'}`, variant: 'success' });
        setConnectionInfo(res);
      } else {
        toast({ title: 'Connection inactive', description: 'No active PayU credentials found.', variant: 'warning' });
      }
    } catch (err: any) {
      showApiError(err, { fallback: { title: 'Test failed' } });
    } finally {
      setTesting(false);
    }
  };

  const handleDisconnect = async () => {
    if (!workspaceScopeId) return;
    setDisconnecting(true);
    try {
      await payuApi.disconnect(workspaceScopeId);
      toast({ variant: "success", title: 'PayU disconnected' });
      setDisconnectDialogOpen(false);
      setConnectionInfo({ connected: false });
      setActiveTab('connect');
    } catch (err: any) {
      showApiError(err, { fallback: { title: 'Disconnect failed' } });
    } finally {
      setDisconnecting(false);
    }
  };

  const handleGenerateLink = async () => {
    const amountCheck = validatePaymentAmount(linkForm.amount);
    if (!amountCheck.ok) {
      toast({ title: 'Invalid amount', description: amountCheck.error, variant: 'warning' });
      return;
    }
    const amount = amountCheck.value;
    if (!linkForm.productinfo.trim()) {
      toast({ title: 'Product info is required', variant: 'warning' });
      return;
    }
    if (!workspaceScopeId) {
      toast({ title: 'No business account selected', variant: 'warning' });
      return;
    }
    setGeneratingLink(true);
    try {
      const res = await payuApi.createPaymentLink(workspaceScopeId, {
        amount,
        productinfo: linkForm.productinfo.trim(),
        firstname: linkForm.firstname.trim() || undefined,
        email: linkForm.email.trim() || undefined,
        phone: linkForm.phone.trim() || undefined,
      });
      if (!res?.success || !res?.link?.checkoutUrl) {
        throw ApiError.fromResult(res, 'No checkout URL returned');
      }
      setGeneratedLink(res.link.checkoutUrl);
      toast({ variant: "success", title: 'Checkout link generated', description: 'Copy the URL to share with your customer.' });
      loadPaymentLinks();
    } catch (err: any) {
      showApiError(err, { fallback: { title: 'Link generation failed' } });
    } finally {
      setGeneratingLink(false);
    }
  };

  const copyLink = () => {
    if (!generatedLink) return;
    navigator.clipboard.writeText(generatedLink);
    toast({ variant: "success", title: 'Link copied to clipboard' });
  };

  const closeLinkDialog = () => {
    setLinkDialogOpen(false);
    setLinkForm({ amount: '', productinfo: '', firstname: '', email: '', phone: '', description: '' });
    setGeneratedLink(null);
  };

  const handleIssueRefund = async () => {
    if (!workspaceScopeId) return;
    if (!refundForm.paymentId.trim()) {
      toast({ title: 'Payment ID required', description: 'Enter the PayU mihpayid.', variant: 'warning' });
      return;
    }
    const parsedAmount = refundForm.amount.trim() ? parseFloat(refundForm.amount) : undefined;
    if (parsedAmount !== undefined && (!Number.isFinite(parsedAmount) || parsedAmount <= 0)) {
      toast({ title: 'Invalid amount', description: 'Amount must be > 0. Leave blank for full refund.', variant: 'warning' });
      return;
    }
    setRefunding(true);
    try {
      const res = await payuApi.refundPayment(workspaceScopeId, {
        paymentId: refundForm.paymentId.trim(),
        amount: parsedAmount,
      });
      if (!res?.success || !res?.refund) {
        throw ApiError.fromResult(res, 'Refund failed');
      }
      setRefundResult(res.refund);
      toast({ title: 'Refund issued', description: `Status: ${res.refund.status}` });
      loadRefunds();
    } catch (err: any) {
      showApiError(err, { fallback: { title: 'Refund failed' } });
    } finally {
      setRefunding(false);
    }
  };

  const closeRefundDialog = () => {
    setRefundDialogOpen(false);
    setRefundForm({ paymentId: '', amount: '', reason: '' });
    setRefundResult(null);
  };

  if (accountsLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {embedded ? null : (
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate('/home/integrations')}>
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div>
          <CardTitle className="text-xl sm:text-2xl">PayU Integration</CardTitle>
          <CardDescription className="text-sm">
            Connect your PayU account to accept UPI, cards, and netbanking
          </CardDescription>
        </div>
      </div>
      )}

      <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as typeof activeTab)} className="space-y-4">
        <TabsList className="inline-flex gap-2 p-2 h-[46px] rounded-[20px]">
          {(
            [
              { value: 'connect', label: 'Connect' },
              { value: 'payments', label: 'Payments' },
              { value: 'links', label: 'Payment Links' },
              { value: 'refunds', label: 'Refunds' },
            ] as const
          ).map((tab) => (
            <TabsTrigger
              key={tab.value}
              value={tab.value}
              disabled={tab.value !== 'connect' && !isConnected}
              className="relative rounded-[12px] data-[state=active]:bg-transparent data-[state=active]:shadow-none"
            >
              <span className="relative z-10">{tab.label}</span>
              {activeTab === tab.value && (
                <motion.div
                  layoutId="payu-tab-pill"
                  className="absolute inset-0 bg-background rounded-[12px] shadow-sm z-0"
                  transition={{ type: 'spring', bounce: 0.2, duration: 0.5 }}
                />
              )}
            </TabsTrigger>
          ))}
        </TabsList>

        <TabsContent value="connect">
          <Card className="border-none">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>{isConnected ? 'Connection Status' : 'Connect PayU'}</CardTitle>
                  <CardDescription>
                    {isConnected ? 'Current PayU connection status' : 'Enter your PayU merchant credentials'}
                  </CardDescription>
                </div>
                {isConnected && (
                  <Button variant="outline" size="sm" onClick={checkConnection} disabled={loadingData || !workspaceScopeId}>
                    <RefreshCw className={`h-4 w-4 mr-2 ${loadingData ? 'animate-spin' : ''}`} />
                    Refresh
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent>
              {loadingData ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin" />
                </div>
              ) : isConnected ? (
                <div className="space-y-4">
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                      <PlugZap className="h-3 w-3 mr-1" />
                      Connected
                    </Badge>
                    <Badge variant="outline" className="capitalize">
                      {connectionInfo.mode === 'prod' ? 'Live' : 'Test'}
                    </Badge>
                    <Badge variant="outline">Manual</Badge>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-muted-foreground">Account Name</Label>
                      <p className="font-medium">{connectionInfo.accountName || 'N/A'}</p>
                    </div>
                    <div>
                      <Label className="text-muted-foreground">Mode</Label>
                      <p className="font-medium capitalize">{connectionInfo.mode === 'prod' ? 'Live (secure.payu.in)' : 'Test (test.payu.in)'}</p>
                    </div>
                    {connectionInfo.connectedAt && (
                      <div>
                        <Label className="text-muted-foreground">Connected At</Label>
                        <p className="font-medium">{new Date(connectionInfo.connectedAt).toLocaleString()}</p>
                      </div>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <Button variant="outline" onClick={handleTestConnection} disabled={testing}>
                      {testing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-2" />}
                      Test Connection
                    </Button>
                    <Button variant="destructive" onClick={() => setDisconnectDialogOpen(true)} disabled={disconnecting}>
                      {disconnecting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Unplug className="h-4 w-4 mr-2" />}
                      Disconnect
                    </Button>
                  </div>

                  <DisconnectConfirmDialog
                    open={disconnectDialogOpen}
                    onOpenChange={setDisconnectDialogOpen}
                    onConfirm={handleDisconnect}
                    isLoading={disconnecting}
                    integration="payu"
                  />
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center py-4 md:py-10">
                  <div className="w-full space-y-8">
                    <div className="space-y-4 text-center">
                      <div className="flex items-center justify-center">
                        <div className="p-4 bg-brand/10 rounded-2xl border border-brand/20 shadow-sm shadow-brand/10">
                          <Key className="h-8 w-8 text-brand" />
                        </div>
                      </div>
                      <div className="space-y-2">
                        <h3 className="text-2xl md:text-4xl font-extrabold tracking-tight text-[#08090A]">Connect PayU</h3>
                        <p className="text-gray-500 text-sm md:text-base max-w-sm mx-auto leading-relaxed">
                          Accept UPI, Credit / Debit Cards, Netbanking, and wallets via PayU's hosted checkout.
                        </p>
                      </div>
                    </div>

                    <Card className="border-none shadow-[0_0_10px_0_rgba(0,0,0,0.06)] rounded-[12px]">
                      <CardContent className="p-6 md:p-8">
                        <form onSubmit={handleManualConnect} className="space-y-6">
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                            <div className="space-y-2.5">
                              <Label htmlFor="payu-mode" className="text-sm font-semibold text-gray-700 ml-1">Mode</Label>
                              <Select value={form.mode} onValueChange={(v) => setForm({ ...form, mode: v as PayUMode })}>
                                <SelectTrigger id="payu-mode" className="h-12 rounded-[12px] bg-gray-50/50 border-gray-200">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="test">Test (test.payu.in)</SelectItem>
                                  <SelectItem value="prod">Live (secure.payu.in)</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                            <div className="space-y-2.5">
                              <Label htmlFor="payu-account-name" className="text-sm font-semibold text-gray-700 ml-1">Account Name (optional)</Label>
                              <Input
                                id="payu-account-name"
                                type="text"
                                placeholder="e.g. Main merchant account"
                                value={form.accountName}
                                onChange={(e) => setForm({ ...form, accountName: e.target.value })}
                                className="h-12 rounded-[12px] bg-gray-50/50 border-gray-200 focus:bg-white focus:border-brand focus:ring-brand/10 transition-all"
                              />
                            </div>
                          </div>

                          <div className="space-y-5">
                            <div className="space-y-2.5">
                              <Label htmlFor="payu-key" className="text-sm font-semibold text-gray-700 ml-1">Merchant Key</Label>
                              <Input
                                id="payu-key"
                                type="text"
                                placeholder="e.g. uH7BhW"
                                value={form.merchantKey}
                                onChange={(e) => setForm({ ...form, merchantKey: e.target.value })}
                                required
                                autoComplete="off"
                                className="h-12 rounded-[12px] bg-gray-50/50 border-gray-200 focus:bg-white focus:border-brand focus:ring-brand/10 transition-all font-mono"
                              />
                            </div>
                            <div className="space-y-2.5">
                              <Label htmlFor="payu-salt" className="text-sm font-semibold text-gray-700 ml-1">Salt (V1, 32-character)</Label>
                              <div className="relative">
                                <Input
                                  id="payu-salt"
                                  type={showSalt ? 'text' : 'password'}
                                  placeholder="••••••••••••••••"
                                  value={form.salt}
                                  onChange={(e) => setForm({ ...form, salt: e.target.value })}
                                  required
                                  autoComplete="off"
                                  className="h-12 rounded-[12px] bg-gray-50/50 border-gray-200 focus:bg-white focus:border-brand focus:ring-brand/10 transition-all pr-12"
                                />
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => setShowSalt((s) => !s)}
                                  className="absolute right-2 top-1/2 -translate-y-1/2 h-8 w-8 p-0"
                                  aria-label={showSalt ? 'Hide salt' : 'Show salt'}
                                >
                                  {showSalt ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                                </Button>
                              </div>
                              <p className="text-xs text-muted-foreground ml-1">
                                Use the V1 salt (labeled "32-bit" in PayU dashboard). V2 / 256-bit salt is not supported.
                              </p>
                            </div>
                          </div>

                          <Button
                            type="submit"
                            className="w-[fit] h-12 bg-brand hover:bg-[#159a5b] text-white rounded-[12px] font-bold text-base shadow-lg shadow-brand/20 transition-all active:scale-[0.98] disabled:opacity-70"
                            disabled={connecting || !workspaceScopeId}
                          >
                            {connecting ? (
                              <>
                                <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                                Connecting to PayU...
                              </>
                            ) : (
                              <>
                                <PlugZap className="h-5 w-5 mr-2" />
                                Connect Account
                              </>
                            )}
                          </Button>
                        </form>
                      </CardContent>
                    </Card>

                    <div className="bg-white rounded-[12px] p-6 md:p-8 space-y-5 shadow-[0_0_10px_0_rgba(0,0,0,0.06)]">
                      <div className="flex items-center gap-3 border-b border-gray-50 pb-4">
                        <div className="flex items-center justify-center w-8 h-8 rounded-full bg-brand/10 text-brand">
                          <span className="font-bold text-sm">i</span>
                        </div>
                        <h4 className="font-bold text-base text-[#08090A]">How to find your PayU credentials</h4>
                      </div>
                      <ol className="space-y-4">
                        {[
                          { text: 'Log in to your', link: 'PayU Dashboard', url: 'https://onboarding.payu.in' },
                          { text: 'Navigate to Developer → API Keys tab' },
                          { text: 'Copy your Merchant Key' },
                          { text: 'Copy your Salt (use V1 / 32-character, NOT V2)' },
                          { text: 'Paste both above and pick the matching mode' },
                        ].map((step, idx) => (
                          <li key={idx} className="flex gap-4 text-sm text-gray-600 group">
                            <span className="flex-shrink-0 flex items-center justify-center w-6 h-6 rounded-md bg-gray-50 text-gray-400 font-bold group-hover:bg-brand/5 group-hover:text-brand transition-colors">
                              {idx + 1}
                            </span>
                            <p className="pt-0.5 leading-relaxed">
                              {step.text}{' '}
                              {step.link && (
                                <a href={step.url} target="_blank" rel="noopener noreferrer" className="text-brand font-bold hover:underline">
                                  {step.link}
                                </a>
                              )}
                            </p>
                          </li>
                        ))}
                      </ol>
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="payments">
          <Card className="border-none">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Payments</CardTitle>
                  <CardDescription>
                    Payments received through your PayU account ({paymentsTotal})
                  </CardDescription>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={loadPayments}
                  disabled={paymentsLoading || !isConnected}
                >
                  <RefreshCw className={`h-4 w-4 mr-2 ${paymentsLoading ? 'animate-spin' : ''}`} />
                  Refresh
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {paymentsLoading ? (
                <div className="flex items-center justify-center py-16">
                  <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                </div>
              ) : payments.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-16 text-center space-y-4">
                  <div className="p-4 bg-slate-100 rounded-2xl">
                    <CreditCard className="h-8 w-8 text-slate-500" />
                  </div>
                  <div className="space-y-2 max-w-md">
                    <h3 className="font-semibold text-lg">No payments yet</h3>
                    <p className="text-sm text-muted-foreground">
                      Payments appear here after the webhook confirms them. Make sure the PayU
                      dashboard webhook URL is set to{' '}
                      <code className="bg-slate-100 px-1.5 py-0.5 rounded text-xs font-mono">
                        /api/v1/payu/webhook
                      </code>
                      .
                    </p>
                  </div>
                </div>
              ) : (
                <div className="overflow-hidden rounded-lg border">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50 text-xs uppercase text-muted-foreground">
                      <tr>
                        <th className="text-left px-4 py-3 font-medium">Txn ID</th>
                        <th className="text-left px-4 py-3 font-medium">Customer</th>
                        <th className="text-left px-4 py-3 font-medium">Product</th>
                        <th className="text-right px-4 py-3 font-medium">Amount</th>
                        <th className="text-left px-4 py-3 font-medium">Status</th>
                        <th className="text-left px-4 py-3 font-medium">Received</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {payments.map((p) => (
                        <tr key={p.id} className="hover:bg-slate-50">
                          <td className="px-4 py-3 font-mono text-xs">
                            <div>{p.txnid}</div>
                            {p.mihpayid && (
                              <div className="text-muted-foreground">mih: {p.mihpayid}</div>
                            )}
                          </td>
                          <td className="px-4 py-3">
                            <div className="font-medium">{p.firstname || '—'}</div>
                            {p.email && (
                              <div className="text-xs text-muted-foreground">{p.email}</div>
                            )}
                          </td>
                          <td className="px-4 py-3">{p.productinfo || '—'}</td>
                          <td className="px-4 py-3 text-right font-medium">
                            ₹{p.amount || '0.00'}
                          </td>
                          <td className="px-4 py-3">
                            <Badge
                              variant="outline"
                              className={
                                p.status === 'captured'
                                  ? 'bg-green-50 text-green-700 border-green-200'
                                  : p.status === 'failed' || p.status === 'cancelled'
                                  ? 'bg-red-50 text-red-700 border-red-200'
                                  : 'bg-amber-50 text-amber-700 border-amber-200'
                              }
                            >
                              {p.status}
                            </Badge>
                          </td>
                          <td className="px-4 py-3 text-xs text-muted-foreground">
                            {p.received_at ? new Date(p.received_at).toLocaleString() : '—'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="links">
          <Card className="border-none">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Payment Links ({paymentLinksTotal})</CardTitle>
                  <CardDescription>Generated checkout links you can share with customers</CardDescription>
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={loadPaymentLinks} disabled={paymentLinksLoading || !isConnected}>
                    <RefreshCw className={`h-4 w-4 mr-2 ${paymentLinksLoading ? 'animate-spin' : ''}`} />
                    Refresh
                  </Button>
                  <Button onClick={() => setLinkDialogOpen(true)} disabled={!isConnected}>
                    <Plus className="h-4 w-4 mr-2" />
                    Generate Link
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {paymentLinksLoading ? (
                <div className="flex items-center justify-center py-16">
                  <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                </div>
              ) : paymentLinks.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-16 text-center space-y-4">
                  <div className="p-4 bg-slate-100 rounded-2xl">
                    <LinkIcon className="h-8 w-8 text-slate-500" />
                  </div>
                  <div className="space-y-2 max-w-md">
                    <h3 className="font-semibold text-lg">No payment links yet</h3>
                    <p className="text-sm text-muted-foreground">
                      Click <span className="font-semibold">Generate Link</span> to create a
                      checkout URL for a specific amount. Share it via WhatsApp, email, or SMS.
                    </p>
                  </div>
                </div>
              ) : (
                <div className="overflow-hidden rounded-lg border">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50 text-xs uppercase text-muted-foreground">
                      <tr>
                        <th className="text-left px-4 py-3 font-medium">Txn ID</th>
                        <th className="text-left px-4 py-3 font-medium">Customer</th>
                        <th className="text-left px-4 py-3 font-medium">Product</th>
                        <th className="text-right px-4 py-3 font-medium">Amount</th>
                        <th className="text-left px-4 py-3 font-medium">Mode</th>
                        <th className="text-left px-4 py-3 font-medium">Created</th>
                        <th className="text-right px-4 py-3 font-medium">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {paymentLinks.map((l) => (
                        <tr key={l.id} className="hover:bg-slate-50">
                          <td className="px-4 py-3 font-mono text-xs">{l.txnid}</td>
                          <td className="px-4 py-3">
                            <div className="font-medium">{l.firstname || '—'}</div>
                            {l.email && <div className="text-xs text-muted-foreground">{l.email}</div>}
                          </td>
                          <td className="px-4 py-3">{l.productinfo || '—'}</td>
                          <td className="px-4 py-3 text-right font-medium">₹{l.amount || '0.00'}</td>
                          <td className="px-4 py-3">
                            <Badge variant="outline" className="capitalize">
                              {l.mode === 'prod' ? 'Live' : 'Test'}
                            </Badge>
                          </td>
                          <td className="px-4 py-3 text-xs text-muted-foreground">
                            {l.created_at ? new Date(l.created_at).toLocaleString() : '—'}
                          </td>
                          <td className="px-4 py-3 text-right">
                            <div className="inline-flex gap-1">
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => {
                                  navigator.clipboard.writeText(l.checkout_url || '');
                                  toast({ variant: "success", title: 'Link copied' });
                                }}
                                aria-label="Copy link"
                                className="h-8 w-8"
                              >
                                <Copy className="h-3.5 w-3.5" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => window.open(l.checkout_url, '_blank')}
                                aria-label="Open link"
                                className="h-8 w-8"
                              >
                                <ExternalLink className="h-3.5 w-3.5" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="refunds">
          <Card className="border-none">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Refunds ({refundsTotal})</CardTitle>
                  <CardDescription>Refund history — issued against completed PayU payments</CardDescription>
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={loadRefunds} disabled={refundsLoading || !isConnected}>
                    <RefreshCw className={`h-4 w-4 mr-2 ${refundsLoading ? 'animate-spin' : ''}`} />
                    Refresh
                  </Button>
                  <Button onClick={() => setRefundDialogOpen(true)} disabled={!isConnected}>
                    <Undo2 className="h-4 w-4 mr-2" />
                    Issue Refund
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {refundsLoading ? (
                <div className="flex items-center justify-center py-16">
                  <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                </div>
              ) : refunds.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-16 text-center space-y-4">
                  <div className="p-4 bg-slate-100 rounded-2xl">
                    <Undo2 className="h-8 w-8 text-slate-500" />
                  </div>
                  <div className="space-y-2 max-w-md">
                    <h3 className="font-semibold text-lg">No refund history yet</h3>
                    <p className="text-sm text-muted-foreground">
                      Click <span className="font-semibold">Issue Refund</span>, paste the PayU{' '}
                      <code className="bg-slate-100 px-1.5 py-0.5 rounded text-xs font-mono">mihpayid</code>,
                      optionally set an amount (blank = full), and confirm.
                    </p>
                  </div>
                </div>
              ) : (
                <div className="overflow-hidden rounded-lg border">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50 text-xs uppercase text-muted-foreground">
                      <tr>
                        <th className="text-left px-4 py-3 font-medium">Payment ID</th>
                        <th className="text-left px-4 py-3 font-medium">Refund Ref</th>
                        <th className="text-right px-4 py-3 font-medium">Amount</th>
                        <th className="text-left px-4 py-3 font-medium">Status</th>
                        <th className="text-left px-4 py-3 font-medium">Message</th>
                        <th className="text-left px-4 py-3 font-medium">Issued</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {refunds.map((r) => (
                        <tr key={r.id} className="hover:bg-slate-50">
                          <td className="px-4 py-3 font-mono text-xs">{r.payment_id}</td>
                          <td className="px-4 py-3 font-mono text-xs">{r.refund_ref_id}</td>
                          <td className="px-4 py-3 text-right font-medium">
                            {r.amount ? `₹${r.amount}` : 'Full'}
                          </td>
                          <td className="px-4 py-3">
                            <Badge
                              variant="outline"
                              className={
                                r.status === 'refunded'
                                  ? 'bg-green-50 text-green-700 border-green-200'
                                  : 'bg-red-50 text-red-700 border-red-200'
                              }
                            >
                              {r.status}
                            </Badge>
                          </td>
                          <td className="px-4 py-3 text-xs text-muted-foreground max-w-xs truncate">
                            {r.message || '—'}
                          </td>
                          <td className="px-4 py-3 text-xs text-muted-foreground">
                            {r.created_at ? new Date(r.created_at).toLocaleString() : '—'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      <Dialog open={refundDialogOpen} onOpenChange={(open) => { if (!open) closeRefundDialog(); else setRefundDialogOpen(true); }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Issue Refund</DialogTitle>
            <DialogDescription>
              Refunds go through the merchant's own PayU account and flow back to the
              customer's original payment method.
            </DialogDescription>
          </DialogHeader>

          {refundResult ? (
            <div className="space-y-4">
              <div className="rounded-lg border bg-green-50 p-4 space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Payment ID</span>
                  <span className="font-mono text-xs">{refundResult.paymentId}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Refund Ref</span>
                  <span className="font-mono text-xs">{refundResult.refundRefId}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Status</span>
                  <span className="font-medium capitalize">{refundResult.status}</span>
                </div>
              </div>
              {refundResult.message && (
                <p className="text-xs text-muted-foreground">{refundResult.message}</p>
              )}
              <p className="text-xs text-muted-foreground">
                Money typically returns to the customer's account in 3-7 business days.
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="refund-payment-id">PayU Payment ID (mihpayid) *</Label>
                <Input
                  id="refund-payment-id"
                  placeholder="e.g. 4032456789"
                  value={refundForm.paymentId}
                  onChange={(e) => setRefundForm({ ...refundForm, paymentId: e.target.value })}
                  className="font-mono"
                />
                <p className="text-xs text-muted-foreground">
                  Find this in the webhook payload (<code className="bg-slate-100 px-1 py-0.5 rounded">mihpayid</code>) or the PayU dashboard transactions page.
                </p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="refund-amount">Amount (INR, optional)</Label>
                <Input
                  id="refund-amount"
                  type="number"
                  min={0}
                  step="0.01"
                  placeholder="Leave blank for full refund"
                  value={refundForm.amount}
                  onChange={(e) => setRefundForm({ ...refundForm, amount: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="refund-reason">Internal note (optional)</Label>
                <Textarea
                  id="refund-reason"
                  placeholder="e.g. Duplicate charge"
                  rows={2}
                  value={refundForm.reason}
                  onChange={(e) => setRefundForm({ ...refundForm, reason: e.target.value })}
                />
                <p className="text-xs text-muted-foreground">
                  Stored in our audit log only — PayU does not accept a refund-reason field.
                </p>
              </div>
            </div>
          )}

          <DialogFooter>
            {refundResult ? (
              <Button onClick={closeRefundDialog}>Done</Button>
            ) : (
              <>
                <Button variant="outline" onClick={closeRefundDialog} disabled={refunding}>Cancel</Button>
                <Button onClick={handleIssueRefund} disabled={refunding}>
                  {refunding && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                  Issue Refund
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={linkDialogOpen} onOpenChange={(open) => { if (!open) closeLinkDialog(); else setLinkDialogOpen(true); }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Generate PayU Checkout Link</DialogTitle>
            <DialogDescription>
              Creates a signed PayU checkout URL. Send it to the customer — when they open it,
              they land on PayU's hosted page and pay directly.
            </DialogDescription>
          </DialogHeader>

          {generatedLink ? (
            <div className="space-y-4">
              <Label className="text-sm font-semibold">Share this URL</Label>
              <div className="flex gap-2">
                <Input value={generatedLink} readOnly className="font-mono text-xs" />
                <Button variant="outline" size="icon" onClick={copyLink} aria-label="Copy link">
                  <Copy className="h-4 w-4" />
                </Button>
                <Button variant="outline" size="icon" onClick={() => window.open(generatedLink, '_blank')} aria-label="Open link">
                  <ExternalLink className="h-4 w-4" />
                </Button>
              </div>
              <p className="text-xs text-muted-foreground">
                This link is long (signed with your merchant key). For a shortened shareable URL,
                pass it through a URL shortener before sending.
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="link-amount">Amount (INR) *</Label>
                  <Input
                    id="link-amount"
                    type="number"
                    min={1}
                    max={MAX_PAYMENT_LINK_AMOUNT}
                    step="0.01"
                    placeholder="100"
                    value={linkForm.amount}
                    onChange={(e) => setLinkForm({ ...linkForm, amount: e.target.value })}
                    aria-invalid={!!linkAmountError}
                  />
                  {linkAmountError ? (
                    <p className="text-sm text-destructive">{linkAmountError}</p>
                  ) : (
                    <p className="text-xs text-muted-foreground">
                      Up to ₹{MAX_PAYMENT_LINK_AMOUNT.toLocaleString('en-IN')}.
                    </p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="link-productinfo">Product info *</Label>
                  <Input
                    id="link-productinfo"
                    placeholder="Pro Plan"
                    value={linkForm.productinfo}
                    onChange={(e) => setLinkForm({ ...linkForm, productinfo: e.target.value })}
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="link-firstname">Customer name</Label>
                  <Input
                    id="link-firstname"
                    placeholder="Alice"
                    value={linkForm.firstname}
                    onChange={(e) => setLinkForm({ ...linkForm, firstname: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="link-email">Email</Label>
                  <Input
                    id="link-email"
                    type="email"
                    placeholder="alice@example.com"
                    value={linkForm.email}
                    onChange={(e) => setLinkForm({ ...linkForm, email: e.target.value })}
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="link-phone">Phone (optional)</Label>
                <Input
                  id="link-phone"
                  type="tel"
                  placeholder="9876543210"
                  value={linkForm.phone}
                  onChange={(e) => setLinkForm({ ...linkForm, phone: e.target.value })}
                />
              </div>
            </div>
          )}

          <DialogFooter>
            {generatedLink ? (
              <Button onClick={closeLinkDialog}>Done</Button>
            ) : (
              <>
                <Button variant="outline" onClick={closeLinkDialog} disabled={generatingLink}>Cancel</Button>
                <Button onClick={handleGenerateLink} disabled={generatingLink || !linkAmountCheck.ok}>
                  {generatingLink && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                  Generate
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Payu;
